import { AppointmentStatus, AppointmentType, Country, defaultPagination, HttpMethod } from 'constants/'
import { useCallback } from 'react'
import { identity } from 'remeda'
import {
  Appointment,
  ProviderScheduleResponse,
  Query,
  QueryResponse,
  ReleasePoolHoldRequest,
  ReleasePoolHoldResponse,
  Resource,
  Shop,
} from 'types'
import { pick, toRequestError } from 'utils'
import { useRequest } from '../useRequest'
import { useServiceCountryUrl } from '../useServiceCountryUrl'

enum ApiPathname {
  APPOINTMENTS = '/api/v1/appointments',
  PROVIDER_SCHEDULE = '/api/v1/resources/:resourceGid/schedule',
  RELEASE_POOL_HOLD = '/api/v1/pools/:poolGid/holds/:resourceGid/release',
  RESOURCES = '/api/v1/resources',
  SHOPS = '/api/v1/shops',
}

type GetAppointmentsQuery = Query<{
  customerGids?: string[]
  dateEnd?: Date
  dateStart?: Date
  opportunityGids?: string[]
  resourceGids?: string[]
  shopGid?: string
  statuses?: AppointmentStatus[]
  types?: AppointmentType[]
}> & {
  customer_gid?: never
  date_end?: never
  date_start?: never
  opportunity_gid?: never
  resource_gid?: never
  shop_gid?: never
  status?: never
  type?: never
}

type GetProviderScheduleQuery = {
  resourceGid: string
  dateStart: Date
  dateEnd: Date
  types?: AppointmentType[]
}

type GetResourcesQuery = Query<{
  gids: string[]
}>

type GetShopsQuery = Query<{
  country?: Country
  id?: string[]
  gid?: string[]
  search?: string
}>

type GetAppointmentsResponse = QueryResponse<Appointment[]>
type GetResourcesResponse = QueryResponse<Resource[]>
type GetShopsResponse = QueryResponse<Shop[]>

interface UseHookContext {
  getAppointments: (query: GetAppointmentsQuery) => Promise<GetAppointmentsResponse>
  getProviderSchedule: (query: GetProviderScheduleQuery) => Promise<ProviderScheduleResponse>
  getResources: (query: GetResourcesQuery) => Promise<GetResourcesResponse>
  getShops: (query: GetShopsQuery) => Promise<GetShopsResponse>
  releasePoolHold: (request: ReleasePoolHoldRequest) => Promise<ReleasePoolHoldResponse>
}

const filterAppointmentsQueryToSearchParams = ({
  customerGids: customer_gid,
  dateEnd,
  dateStart,
  opportunityGids: opportunity_gid,
  resourceGids: resource_gid,
  shopGid: shop_gid,
  statuses: status,
  types: type,
  ...rest
}: GetAppointmentsQuery) => ({
  ...rest,
  customer_gid,
  date_end: dateEnd?.toISOString(),
  date_start: dateStart?.toISOString(),
  opportunity_gid,
  resource_gid,
  shop_gid,
  status,
  type,
})

const filterProviderScheduleQueryToRouteParams = ({ resourceGid }: GetProviderScheduleQuery) => ({ resourceGid })

const filterProviderScheduleQueryToSearchParams = ({ dateStart, dateEnd, types }: GetProviderScheduleQuery) => ({
  date_end: dateEnd.toISOString(),
  date_start: dateStart.toISOString(),
  type: types,
})

// useRequest lets a 404 through as success, and on this route a 404 is a refusal
const rejectNotFoundAsRefusal = async (response: Response) => {
  if (!response.ok) {
    throw toRequestError(await response.text(), response.status)
  }

  return response.json()
}

const filterResourcesQueryToSearchParams = ({ gids }: GetResourcesQuery) => ({
  gid: gids,
})

const useAppointmentsService = (): UseHookContext => {
  const { url: baseUrl } = useServiceCountryUrl('appointment')

  const getAppointmentsRequest = useRequest<GetAppointmentsQuery, GetAppointmentsResponse>({
    baseUrl,
    filterQueryToSearchParams: filterAppointmentsQueryToSearchParams,
    pathname: ApiPathname.APPOINTMENTS,
  })

  const getAppointments = useCallback(
    (query: GetAppointmentsQuery) => {
      if (!hasAppointmentFilters(query as unknown as Record<string, unknown>)) {
        console.warn('[useAppointmentsService] getAppointments called without filters — returning empty results')

        return Promise.resolve<GetAppointmentsResponse>({
          data: [],
          meta: { items_total: 0, page: 0, page_size: 0, pages_total: 0 },
        })
      }

      return getAppointmentsRequest({
        ...defaultPagination,
        ...query,
      })
    },
    [getAppointmentsRequest]
  )

  const getProviderSchedule = useRequest<GetProviderScheduleQuery, ProviderScheduleResponse>({
    baseUrl,
    filterQueryToRouteParams: filterProviderScheduleQueryToRouteParams,
    filterQueryToSearchParams: filterProviderScheduleQueryToSearchParams,
    pathname: ApiPathname.PROVIDER_SCHEDULE,
  })

  const getResourcesRequest = useRequest<GetResourcesQuery, GetResourcesResponse>({
    baseUrl,
    filterQueryToSearchParams: filterResourcesQueryToSearchParams,
    pathname: ApiPathname.RESOURCES,
  })

  const getResources = useCallback(
    (query: GetResourcesQuery) =>
      getResourcesRequest({
        ...defaultPagination,
        ...query,
      }),
    [getResourcesRequest]
  )

  const getShopsRequest = useRequest<GetShopsQuery, GetShopsResponse>({
    baseUrl,
    filterQueryToSearchParams: identity,
    pathname: ApiPathname.SHOPS,
  })

  const getShops = useCallback(
    (query: GetShopsQuery) =>
      getShopsRequest({
        ...defaultPagination,
        ...query,
      }),
    [getShopsRequest]
  )

  const releasePoolHold = useRequest<ReleasePoolHoldRequest, ReleasePoolHoldResponse>({
    baseUrl,
    filterQueryToRequestBody: pick(['reason']),
    filterQueryToRouteParams: pick(['poolGid', 'resourceGid']),
    method: HttpMethod.PATCH,
    pathname: ApiPathname.RELEASE_POOL_HOLD,
    transformResponse: rejectNotFoundAsRefusal,
  })

  return { getAppointments, getProviderSchedule, getResources, getShops, releasePoolHold }
}

const PAGINATION_AND_SORT_KEYS = new Set([
  'limit',
  'offset',
  'page',
  'pageSize',
  'page_size',
  'sort',
  'sortBy',
  'sort_by',
])

const hasAppointmentFilters = (query: Record<string, unknown>) =>
  Object.entries(query).some(([key, value]) => {
    if (PAGINATION_AND_SORT_KEYS.has(key)) return false

    return value !== undefined && value !== null && (!Array.isArray(value) || value.length > 0)
  })

export {
  filterAppointmentsQueryToSearchParams,
  filterProviderScheduleQueryToRouteParams,
  filterProviderScheduleQueryToSearchParams,
  hasAppointmentFilters,
  useAppointmentsService,
}
export type { GetProviderScheduleQuery, GetResourcesQuery, GetShopsQuery }
